import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { LogoLockup } from '../components/Icons';
import { registrarVisita } from '../utils/visitas';

// Página pública (sin login): el cliente de un estudio consulta el estado de su
// caso con el código público + su RUT. Funciona en cualquier dominio de Tabularis.
const C = { navy: '#0D1B35', blue: '#1A3A6E', blue2: '#2756A8', line: '#DDE6F3', muted: '#6B82A0', ok: '#1F7A52' };

type Paso = { nombre: string; done: boolean; actual: boolean };
type Cuota = { concepto: string; vencimiento: string | null; monto: number; estado: string; fechaPago: string | null; formaPago: string | null };
type Financiero = { total: number; pagado: number; saldo: number; cuotas: Cuota[] };
type Audiencia = { tipo: string; fecha: string; hora: string | null; lugar: string | null };
// Lo que el abogado le escribió al cliente: "Recordatorio de documentos
// faltantes" (Documentos) y "Recordatorio para cliente" (Datos) del panel.
type Recordatorios = { general: string; documentos: string };
type Estado = {
  estudio: string; codigo: string; cliente: string; tema: string | null; estado: string | null;
  etapa: string | null; pasos?: Paso[]; financiero?: Financiero; audiencias?: Audiencia[];
  recordatorios?: Recordatorios; evaluacion?: string | null; actualizado: string;
};

const fmtFecha = (s: string) => { try { return new Date(s).toLocaleDateString('es-CL', { day: '2-digit', month: 'long', year: 'numeric' }); } catch { return ''; } };
// Fecha corta para tablas: acepta "YYYY-MM-DD" o "DD-MM-YYYY".
const fmtCorta = (s: string | null) => {
  if (!s) return '—';
  const m1 = s.match(/^(\d{4})-(\d{2})-(\d{2})/); if (m1) return `${m1[3]}-${m1[2]}-${m1[1]}`;
  const m2 = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/); if (m2) return `${m2[1].padStart(2, '0')}-${m2[2].padStart(2, '0')}-${m2[3]}`;
  return s;
};
const fmtMoney = (n: number) => '$' + Number(n || 0).toLocaleString('es-CL');

// La portada deja el código y el RUT en sessionStorage cuando el cliente ya
// los escribió allá, y esta página los consume al cargar: se consulta de
// inmediato y se borran. No van por la URL para que el RUT no quede en el
// historial, en los registros de CloudFront ni en un enlace que se reenvíe.
export const CONSULTA_PENDIENTE = 'tabularis_consulta_estado';
const desdeSesion = () => {
  try {
    const raw = sessionStorage.getItem(CONSULTA_PENDIENTE);
    sessionStorage.removeItem(CONSULTA_PENDIENTE);
    const v = raw ? JSON.parse(raw) : {};
    return { codigo: String(v.codigo || '').toUpperCase(), rut: String(v.rut || '') };
  } catch { return { codigo: '', rut: '' }; }
};

const EstadoPublico: React.FC = () => {
  const [inicial] = useState(desdeSesion);
  const [codigo, setCodigo] = useState(inicial.codigo);
  const [rut, setRut] = useState(inicial.rut);
  const [res, setRes] = useState<Estado | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const consultarDatos = async (cod: string, r: string) => {
    setError(''); setRes(null); setLoading(true);
    try {
      const resp = await api<Estado>('/publico/estado', { method: 'POST', auth: false, body: { codigo: cod.trim(), rut: r.trim() } });
      setRes(resp);
    } catch (err: any) {
      setError(err?.status === 404 ? 'No encontramos un caso con esos datos. Revisa el código y el RUT.' : 'No se pudo consultar. Intenta de nuevo en un momento.');
    } finally { setLoading(false); }
  };
  const consultar = (e: React.FormEvent) => { e.preventDefault(); consultarDatos(codigo, rut); };

  useEffect(() => { registrarVisita('estado'); }, []);
  useEffect(() => {
    if (!inicial.codigo || !inicial.rut) return;
    consultarDatos(inicial.codigo, inicial.rut);
    // Solo al cargar: los valores iniciales no cambian después.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', minHeight: '100vh', background: '#F5F8FC', color: C.navy, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '5vh 20px' }}>
      <a href="/" style={{ display: 'flex', alignItems: 'center', gap: 10, color: C.blue, textDecoration: 'none', marginBottom: 26 }}>
        <LogoLockup height={28} />
      </a>

      <div style={{ width: '100%', maxWidth: res ? 760 : 440, transition: 'max-width 0.25s', background: '#fff', border: `1px solid ${C.line}`, borderRadius: 16, padding: '30px 28px', boxShadow: '0 12px 34px rgba(13,27,53,0.08)' }}>
        <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: 24, margin: 0, color: C.navy }}>Consulta el estado de tu caso</h1>
        <p style={{ color: C.muted, fontSize: 14, marginTop: 8, lineHeight: 1.55 }}>Ingresa el código que te dio tu abogado y tu RUT.</p>

        <form onSubmit={consultar} style={{ marginTop: 20 }}>
          <label style={lbl}>Código del caso</label>
          <input style={inp} value={codigo} onChange={(e) => setCodigo(e.target.value.toUpperCase())} placeholder="SVA-7K2M9" required />
          <label style={{ ...lbl, marginTop: 14 }}>Tu RUT</label>
          <input style={inp} value={rut} onChange={(e) => setRut(e.target.value)} placeholder="12.345.678-9" required />
          {error && <div style={{ background: '#FDEDED', color: '#8A2A2A', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginTop: 14 }}>{error}</div>}
          <button type="submit" disabled={loading} style={{ width: '100%', marginTop: 18, background: C.blue, color: '#fff', border: 'none', borderRadius: 9, padding: '12px', fontSize: 15, fontWeight: 600, cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.7 : 1 }}>
            {loading ? 'Consultando…' : 'Consultar estado'}
          </button>
        </form>

        {res && (
          <div style={{ marginTop: 22, borderTop: `1px solid ${C.line}`, paddingTop: 20 }}>
            {/* Encabezado */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 13, color: C.muted }}>{res.estudio}</div>
                <div style={{ fontSize: 18, fontWeight: 700, marginTop: 2, fontFamily: 'Playfair Display, serif' }}>Hola{res.cliente ? `, ${res.cliente}` : ''} 👋</div>
                {res.tema && <div style={{ fontSize: 13, color: C.muted, marginTop: 2 }}>{res.tema} · Código {res.codigo}</div>}
              </div>
              {res.estado && <span style={{ fontSize: 12, fontWeight: 700, color: C.ok, background: '#E6F3EC', border: `1px solid #BFE3CD`, borderRadius: 20, padding: '5px 12px', whiteSpace: 'nowrap' }}>{res.estado}</span>}
            </div>

            {/* Lo que necesitamos de ti: mismo texto y mismo título que los
                correos al cliente, para que no parezca otra cosa. Va primero
                porque es lo único de la página en que el cliente tiene que
                hacer algo. */}
            {res.recordatorios && (res.recordatorios.documentos || res.recordatorios.general) && (
              <div style={{ marginTop: 22 }}>
                <Titulo>Lo que necesitamos de ti</Titulo>
                {res.recordatorios.documentos && (
                  <div style={{ border: '1px solid #EBD9AE', background: '#FDF6E6', borderRadius: 10, padding: '12px 14px', marginTop: 10 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.navy }}>📋 Documentos pendientes</div>
                    <div style={{ fontSize: 13.5, color: C.navy, marginTop: 6, lineHeight: 1.6, whiteSpace: 'pre-line' }}>{res.recordatorios.documentos}</div>
                  </div>
                )}
                {res.recordatorios.general && (
                  <div style={{ border: `1px solid ${C.line}`, background: '#FAFCFF', borderRadius: 10, padding: '12px 14px', marginTop: 10 }}>
                    <div style={{ fontSize: 13.5, color: C.navy, lineHeight: 1.6, whiteSpace: 'pre-line' }}>{res.recordatorios.general}</div>
                  </div>
                )}
              </div>
            )}

            {/* La evaluación que el abogado escribió para el cliente en la
                ficha ("Evaluación (visible para cliente)"). Llega como texto
                plano: aquí nunca se pinta HTML. */}
            {res.evaluacion && (
              <div style={{ marginTop: 22 }}>
                <Titulo>Evaluación de tu caso</Titulo>
                <div style={{ border: `1px solid ${C.line}`, background: '#FAFCFF', borderRadius: 10, padding: '12px 14px', marginTop: 10 }}>
                  <div style={{ fontSize: 13.5, color: C.navy, lineHeight: 1.6, whiteSpace: 'pre-line' }}>{res.evaluacion}</div>
                </div>
              </div>
            )}

            {/* Etapas del procedimiento */}
            {res.pasos && res.pasos.length > 0 && (
              <div style={{ marginTop: 22 }}>
                <Titulo>Etapas del procedimiento</Titulo>
                <div style={{ display: 'grid', gap: 2, marginTop: 10 }}>
                  {res.pasos.map((p, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0' }}>
                      <span style={{ width: 22, height: 22, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700,
                        background: p.done ? C.ok : p.actual ? C.blue : '#EEF3FA', color: (p.done || p.actual) ? '#fff' : C.muted, border: p.actual ? `2px solid ${C.blue}` : 'none' }}>
                        {p.done ? '✓' : i + 1}
                      </span>
                      <span style={{ fontSize: 14, fontWeight: p.actual ? 700 : 500, color: p.actual ? C.navy : p.done ? C.navy : C.muted }}>
                        {p.nombre}{p.actual && <span style={{ color: C.blue, fontWeight: 700 }}> — EN CURSO</span>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Estado financiero */}
            {res.financiero && res.financiero.cuotas.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <Titulo>Estado financiero del caso</Titulo>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginTop: 10 }}>
                  <Card label="Total acordado" valor={fmtMoney(res.financiero.total)} color={C.navy} />
                  <Card label="Pagado" valor={fmtMoney(res.financiero.pagado)} color={C.ok} />
                  <Card label="Saldo pendiente" valor={fmtMoney(res.financiero.saldo)} color="#B8702A" />
                </div>
                <div style={{ overflowX: 'auto', marginTop: 14 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520, fontSize: 13 }}>
                    <thead>
                      <tr style={{ color: C.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', textAlign: 'left' }}>
                        <th style={th}>Cuota</th><th style={th}>Vencimiento</th><th style={{ ...th, textAlign: 'right' }}>Valor</th><th style={th}>Estado</th><th style={th}>Fecha pago</th><th style={th}>Forma</th>
                      </tr>
                    </thead>
                    <tbody>
                      {res.financiero.cuotas.map((q, i) => {
                        const pagada = String(q.estado).toLowerCase() === 'pagado';
                        return (
                          <tr key={i} style={{ borderTop: `1px solid ${C.line}` }}>
                            <td style={td}>{q.concepto}</td>
                            <td style={td}>{fmtCorta(q.vencimiento)}</td>
                            <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(q.monto)}</td>
                            <td style={td}><span style={{ fontSize: 11.5, fontWeight: 600, padding: '2px 8px', borderRadius: 12, background: pagada ? '#E6F3EC' : '#FBF0E4', color: pagada ? C.ok : '#B8702A' }}>{q.estado}</span></td>
                            <td style={{ ...td, color: C.muted }}>{fmtCorta(q.fechaPago)}</td>
                            <td style={{ ...td, color: C.muted }}>{q.formaPago || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Audiencias programadas */}
            {res.audiencias && res.audiencias.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <Titulo>Audiencias programadas</Titulo>
                <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
                  {res.audiencias.map((a, i) => (
                    <div key={i} style={{ border: `1px solid ${C.line}`, borderRadius: 10, padding: '12px 14px' }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>{a.tipo}</div>
                      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 6, fontSize: 13, color: C.navy }}>
                        <span>📅 {fmtCorta(a.fecha)}</span>
                        {a.hora && <span>🕘 {a.hora}</span>}
                        {a.lugar && <span style={{ color: C.blue2, wordBreak: 'break-word' }}>📍 {a.lugar}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 22, fontSize: 12.5, color: C.muted, flexWrap: 'wrap' }}>
              <span>Última actualización: {fmtFecha(res.actualizado)}</span>
            </div>
            <p style={{ fontSize: 12, color: C.muted, marginTop: 10, lineHeight: 1.5 }}>
              ¿Dudas sobre tu caso? Contacta directamente a tu abogado en <strong>{res.estudio}</strong>.
            </p>
          </div>
        )}
      </div>

      <div style={{ color: C.muted, fontSize: 12.5, marginTop: 20 }}>Tabularis · Gestión legal para estudios de Chile</div>
    </div>
  );
};

const Titulo: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: '#2756A8' }}>{children}</div>
);
const Card: React.FC<{ label: string; valor: string; color: string }> = ({ label, valor, color }) => (
  <div style={{ border: '1px solid #DDE6F3', borderRadius: 10, padding: '12px 14px', background: '#FAFCFF' }}>
    <div style={{ fontSize: 11, color: '#6B82A0', textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
    <div style={{ fontSize: 19, fontWeight: 700, color, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{valor}</div>
  </div>
);

const th: React.CSSProperties = { padding: '6px 10px', fontWeight: 600 };
const td: React.CSSProperties = { padding: '8px 10px', verticalAlign: 'middle' };
const lbl: React.CSSProperties = { display: 'block', fontSize: 13, color: '#6B82A0', marginBottom: 5 };
const inp: React.CSSProperties = { width: '100%', padding: '11px 12px', border: '1px solid #DDE6F3', borderRadius: 9, fontSize: 15, fontFamily: 'Inter, sans-serif', color: '#0D1B35', boxSizing: 'border-box' };

export default EstadoPublico;
